import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useLocation } from 'react-router-dom'
import { CommonTable } from '@/components/common'
import { chairpersonService } from '@/services'
import { formatUtcDate, formatDisplayTime } from '@/utils/date'
import { DriveDonorsTabShell } from './DriveDonorsTabShell'
import { DonorsSelectionToolbar } from './DonorsSelectionToolbar'
import { DonorsExportButton } from './DonorsExportButton'
import {
  DRIVE_DONORS_EMAIL_SOURCE,
  DRIVE_DONORS_RECIPIENT_MODES,
  DRIVE_DONORS_SELECTED_RECIPIENT_ERROR,
  DRIVE_DONORS_SOURCE_TABS,
  DONOR_EXPORT_CONTACT_HEADERS,
  donationStatusLabel,
  hasDriveDonorsSelectedRecipients,
  navigateToDriveDonorsEmailFlow,
} from './constants'
import { pickChairpersonDriveNavState } from '@/utils/chairpersonNavigation'

const CSV_HEADERS = [
  { key: 'donor_name', label: 'Donor' },
  { key: 'donor_number', label: 'Donor Number' },
  { key: 'procedure_name', label: 'Procedure' },
  { key: 'donation_status', label: 'Outcome' },
  { key: 'donation_time', label: 'Time' },
  ...DONOR_EXPORT_CONTACT_HEADERS,
]

export const DriveAttendeesTab = ({ driveId, accountId, driveHasOccurred }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [rows, setRows] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedRows, setSelectedRows] = useState([])
  const [allDonorsCount, setAllDonorsCount] = useState(null)
  const [loadingSendOptions, setLoadingSendOptions] = useState(false)
  const [emailFlowError, setEmailFlowError] = useState('')
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 25,
    sortBy: undefined,
    sortDirection: undefined,
  })

  const fetchAttendees = useCallback(async () => {
    if (!driveId || !accountId) return

    if (!driveHasOccurred) {
      setRows([])
      setTotalRecords(0)
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = await chairpersonService.getDriveDonorsDriveAttendees(
        driveId,
        accountId,
        {
          page: pagination.page + 1,
          limit: pagination.perPage,
        }
      )
      const payload = response?.data ?? {}
      setRows(Array.isArray(payload.attendees) ? payload.attendees : [])
      setTotalRecords(Number(payload.total_count ?? 0))
    } catch (error) {
      console.error('Error fetching drive attendees', error)
      setRows([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }, [
    driveId,
    accountId,
    driveHasOccurred,
    pagination.page,
    pagination.perPage,
  ])

  useEffect(() => {
    fetchAttendees()
  }, [fetchAttendees])

  useEffect(() => {
    setSelectedRows([])
  }, [pagination.page, pagination.perPage, driveId])

  useEffect(() => {
    if (!driveId || !accountId || !driveHasOccurred) {
      setAllDonorsCount(null)
      return undefined
    }

    let cancelled = false

    const loadSendCounts = async () => {
      setLoadingSendOptions(true)
      try {
        const preview =
          await chairpersonService.previewDriveDonorsEmailRecipients(
            driveId,
            accountId,
            {
              source_tab: DRIVE_DONORS_SOURCE_TABS.DRIVE_ATTENDEES,
              source: DRIVE_DONORS_EMAIL_SOURCE,
              recipient_mode: DRIVE_DONORS_RECIPIENT_MODES.ALL_DONORS,
            }
          )

        if (!cancelled) {
          setAllDonorsCount(Number(preview?.data?.will_receive) || 0)
        }
      } catch (error) {
        if (!cancelled) {
          setAllDonorsCount(null)
        }
      } finally {
        if (!cancelled) {
          setLoadingSendOptions(false)
        }
      }
    }

    loadSendCounts()

    return () => {
      cancelled = true
    }
  }, [accountId, driveHasOccurred, driveId])

  const tableData = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        rowKey: `${row.donor_id}-${row.donation_date}-${index}`,
        donor_name: row.donor_name ?? 'N/A',
        donor_number: row.donor_number ?? 'N/A',
        procedure_name: row.procedure_name ?? 'N/A',
        donation_status: donationStatusLabel(row.donation_status),
        donation_time: row.donation_date
          ? formatDisplayTime(row.donation_date, 'hh:mm a')
          : 'N/A',
      })),
    [rows]
  )

  const selectedDonorIds = useMemo(
    () => selectedRows.map(row => String(row.donor_id)).filter(Boolean),
    [selectedRows]
  )

  const allSelected =
    tableData.length > 0 && selectedRows.length === tableData.length

  const handleToggleSelectAll = () => {
    setSelectedRows(allSelected ? [] : [...tableData])
  }

  const driveNavState = pickChairpersonDriveNavState(location.state)

  const startEmailFlow = useCallback(
    recipientMode => {
      const donorIds =
        recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED
          ? selectedRows.map(row => String(row.donor_id)).filter(Boolean)
          : []

      if (
        recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED &&
        !hasDriveDonorsSelectedRecipients({ donorIds })
      ) {
        setEmailFlowError(DRIVE_DONORS_SELECTED_RECIPIENT_ERROR)
        return
      }

      setEmailFlowError('')
      navigateToDriveDonorsEmailFlow({
        navigate,
        driveId,
        accountId,
        donorIds,
        guestIds: [],
        sourceTab: DRIVE_DONORS_SOURCE_TABS.DRIVE_ATTENDEES,
        recipientMode,
        driveNavState,
      })
    },
    [accountId, driveId, driveNavState, navigate, selectedRows]
  )

  const fetchExportData = useCallback(async () => {
    const response = await chairpersonService.getDriveDonorsDriveAttendees(
      driveId,
      accountId,
      { forExport: 'true' }
    )
    const exportRows = Array.isArray(response?.data?.attendees)
      ? response.data.attendees
      : []

    return exportRows.map(row => ({
      donor_name: row.donor_name,
      donor_number: row.donor_number || 'N/A',
      procedure_name: row.procedure_name || 'N/A',
      donation_status: donationStatusLabel(row.donation_status),
      donation_time: row.donation_date
        ? formatDisplayTime(row.donation_date, 'hh:mm a')
        : 'N/A',
      email: row.email || '',
      phone: row.phone || '',
      calling_preference: row.calling_preference || '',
      text_preference: row.text_preference || '',
      email_preference: row.email_preference || '',
    }))
  }, [driveId, accountId])

  const columns = [
    { id: 'donor_name', label: 'Donor', minWidth: 180 },
    { id: 'donor_number', label: 'Donor Number', minWidth: 120 },
    { id: 'procedure_name', label: 'Procedure', minWidth: 140 },
    { id: 'donation_status', label: 'Outcome', minWidth: 160 },
    { id: 'donation_time', label: 'Time', minWidth: 180 },
  ]

  if (!driveHasOccurred) {
    return (
      <DriveDonorsTabShell
        statTitle='Drive Attendees (0)'
        statSubtitle='Donors who attended this drive'
        showEligibilityBanner={false}
        aboutTitle='About Drive Attendees'
        aboutText='This drive has not occurred yet. Drive attendees will appear here after the drive date. For upcoming drives, this list will populate once the drive has occurred.'
      />
    )
  }

  return (
    <DriveDonorsTabShell
      statTitle={`Drive Attendees (${totalRecords})`}
      statSubtitle='Donors who attended this drive'
      showEligibilityBanner={false}
      aboutTitle='About Drive Attendees'
      aboutText='This list includes donors who attended this drive, including donation outcomes.'
      selectionToolbar={
        <DonorsSelectionToolbar
          selectedCount={selectedDonorIds.length}
          selectionActionsDisabled={selectedDonorIds.length === 0}
          allSelected={allSelected}
          onToggleSelectAll={handleToggleSelectAll}
          onSendToSelected={() =>
            startEmailFlow(DRIVE_DONORS_RECIPIENT_MODES.SELECTED)
          }
          onSendToAllDonors={() =>
            startEmailFlow(DRIVE_DONORS_RECIPIENT_MODES.ALL_DONORS)
          }
          onClearSelection={() => setSelectedRows([])}
          allDonorsCount={allDonorsCount}
          loadingSendOptions={loadingSendOptions}
          showEligibleOption={false}
        />
      }
      exportAction={
        <DonorsExportButton
          disabled={loading || !totalRecords}
          fetchExportData={fetchExportData}
          csvHeaders={CSV_HEADERS}
          csvFilename={`Drive_Attendees_${driveId}.csv`}
        />
      }
      errorMessage={emailFlowError}
    >
      <CommonTable
        columns={columns}
        data={tableData}
        loading={loading}
        mode='server'
        totalItems={totalRecords}
        showSelect
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageSizeOptions={[10, 25, 50]}
      />
    </DriveDonorsTabShell>
  )
}

DriveAttendeesTab.propTypes = {
  driveId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  driveHasOccurred: PropTypes.bool,
}
